import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

interface SenderMatchConditionEditPanelProps {
  isOpen: boolean;
  onClose: () => void;
  value: string[];
  onChange: (value: string[]) => void;
}

export function SenderMatchConditionEditPanel({ 
  isOpen, 
  onClose, 
  value, 
  onChange 
}: SenderMatchConditionEditPanelProps) {
  const [newItem, setNewItem] = useState("");
  const [items, setItems] = useState<string[]>(value || []);

  const handleAddItem = () => {
    if (newItem.trim() && !items.includes(newItem.trim())) {
      const updatedItems = [...items, newItem.trim()];
      setItems(updatedItems);
      setNewItem("");
    }
  };

  const handleRemoveItem = (index: number) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleAddItem();
    }
  };

  const handleSave = () => {
    onChange(items);
    onClose();
  };

  const handleCancel = () => {
    setItems(value || []);
    setNewItem("");
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle>Edit Sender Email Patterns</SheetTitle>
          <SheetDescription>
            Add email addresses and patterns to match against the sender. Use * as a wildcard (e.g., *@company.com, user@*).
          </SheetDescription>
        </SheetHeader>
        
        <div className="space-y-4 mt-6">
          <div className="flex gap-2">
            <Input
              placeholder="e.g., john@example.com, *@company.com, user@*"
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              onKeyPress={handleKeyPress}
              className="flex-1"
            />
            <Button onClick={handleAddItem} disabled={!newItem.trim()}>
              Add
            </Button>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-medium">Current Patterns</h4>
            
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No items added yet
              </p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {items.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-muted/50 rounded-md"
                  >
                    <span className="text-sm text-foreground">{item}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveItem(index)}
                      className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex gap-2 mt-6 pt-4 border-t">
          <Button variant="outline" onClick={handleCancel} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleSave} className="flex-1" disabled={items.length === 0}>
            Save
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}